/* Companio horizontal rail — continuous marquee with an optional step button.

   SHARED. Used by companio-product-shelf, companio-moments, companio-trust-bar
   and companio-story (the follow strip). It replaced assets/companio-shelf.js on
   2026-08-17 so the rails cannot drift apart; if you are about to copy this
   logic into a fifth section, add data-rail to that section instead.
   NOTE: this is NOT a global script. Every section that uses it loads it with
   its own <script> tag. A new page with perfect markup and no script tag looks
   exactly like a broken rail with no console error (window F shipped that once).

   NOT FROM FIGMA. The designer supplied no motion spec; behaviour was agreed
   with William: constant-speed leftward drift, seamless loop, pause on hover,
   plus — where a data-rail-next button exists — one click per card.

   ==========================================================================
   ONLY A TICKER CLONES. (2026-08-31, window G — this is a behaviour change.)
   ==========================================================================
   A seamless loop is built by CLONING the whole set and appending it. That is
   right for decoration and wrong for content, and the difference is not a
   matter of taste:

     data-rail-fill="true"  — a ticker. The set is repeated until it is at least
       twice the container width so it always scrolls, and the repeats ARE the
       effect. Used by companio-trust-bar (four claims) and the companio-story
       follow strip (photo band). Nobody counts the items in a ticker.

     no data-rail-fill    — a finite list of real records. NEVER CLONED.
       Used by companio-product-shelf (products) and companio-moments (real
       customer posts). Cloning four products makes the page show eight, and a
       shopper scrolling the row meets the same four socks twice. The clones
       carry aria-hidden, so a screen reader hears four and a sighted visitor
       sees eight — which is worse than either on its own.

   This is the same error window E found on the Gifts page, where three real
   gift boxes rendered as six (handover 11.41). That one was fixed in the
   section by switching to a grid; nobody checked the home page, where the
   product shelf and the UGC row had been doing it since they were built.
   Measured at 1440 on 2026-08-31: track 3184px wide, 8 cards, 4 of them clones
   at full 372px. Fixed here rather than in the two sections so it cannot come
   back the next time somebody adds data-rail to something.

   WHAT A NON-CLONING RAIL DOES INSTEAD. It is a real scroll container with a
   step button. There is no auto-drift, because drifting without a loop just
   runs into the end and stops. The arrow advances one card and returns to the
   start once it reaches the end — so the row still ends up back where it
   began, without inventing any products to get there. The `autoplay` and
   `autoplay_speed` settings on those two sections therefore do nothing today;
   they are left in place because they take effect the moment a section opts
   into fill mode. If you want the home row moving on its own again, the honest
   fix is more real products, not more clones.

   HOW THE LOOP WORKS (fill mode only)
   The track is a real overflow-x scroll container, so with JS blocked it is
   still a swipeable rail. We clone the set and append it, then drive native
   scrollLeft from requestAnimationFrame. When the position passes the offset of
   the first clone we subtract that distance — the pixels either side of the
   seam are identical, so the jump is invisible.

   KEEP THE POSITION IN `pos`, NEVER READ IT BACK FROM scrollLeft.
   This is the bug that shipped first time and made the drift look completely
   dead while the button still worked. At 24px/s a frame advances ~0.4px. Doing
   `track.scrollLeft += 0.4` reads a rounded-down integer, adds 0.4, writes it,
   and the browser rounds it away again — so the rail never leaves 0.
   Accumulating in a float and only ever WRITING to scrollLeft makes the
   rounding harmless. The button was unaffected because it moves ~396px.

   `expected` lets us tell our own writes apart from a real user scroll so that
   `pos` re-syncs instead of yanking the rail back.

   Clones are aria-hidden with tabindex -1 so screen readers and keyboard users
   only ever meet each item once. companio-ugc.js listens for
   companio:rail:cloned and swaps media inside clones for their posters, so a
   duplicated tile never costs a second video stream. That listener is now only
   reached by fill-mode rails; companio-moments no longer clones at all, which
   also means it no longer needs it.

   Motion is suppressed under prefers-reduced-motion, when the section is
   scrolled out of view, and when the tab is hidden. The step button keeps
   working in all of those cases because it is user-initiated.
   (Debugging note: requestAnimationFrame is PAUSED while a browser panel is
   hidden, so a rail read from a collapsed devtools/preview pane looks frozen
   even when it is fine. Bring the tab to the front before calling it broken.)
*/
(function () {
  'use strict';

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  var STEP_MS = 420;
  var MAX_FILL_PASSES = 12;

  function initRail(root) {
    if (root.dataset.railReady) return;
    root.dataset.railReady = '1';

    var track = root.querySelector('[data-rail-track]');
    if (!track || track.children.length === 0) return;

    var nextBtn = root.querySelector('[data-rail-next]');
    var speed = parseFloat(root.dataset.railSpeed);
    if (!speed || speed < 0) speed = 24;
    var autoplay = root.dataset.railAutoplay !== 'false';
    /* fill is now the ONLY thing that turns cloning on. See the header. */
    var fill = root.dataset.railFill === 'true';

    var originalCount = track.children.length;
    var period = 0;
    var cloned = false;
    var paused = false;
    var stepping = false;
    var onScreen = true;
    var resumeTimer = null;
    var lastTime = null;
    var pos = track.scrollLeft;
    var expected = -1;

    function write(value) {
      track.scrollLeft = value;
      expected = track.scrollLeft;
    }

    function hasOverflow() {
      return track.scrollWidth - track.clientWidth > 4;
    }

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    function appendSet(originals) {
      originals.forEach(function (el) {
        var copy = el.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('data-rail-clone', '');
        if (copy.hasAttribute('href')) copy.setAttribute('tabindex', '-1');
        Array.prototype.forEach.call(
          copy.querySelectorAll('a, button, input, select, textarea'),
          function (node) { node.setAttribute('tabindex', '-1'); }
        );
        track.appendChild(copy);
      });
    }

    function buildClones() {
      if (cloned) return;
      var originals = Array.prototype.slice.call(track.children, 0, originalCount);
      var passes = 0;
      do {
        appendSet(originals);
        passes++;
      } while (track.scrollWidth < track.clientWidth * 2 && passes < MAX_FILL_PASSES);
      cloned = true;
      root.dispatchEvent(new CustomEvent('companio:rail:cloned', { bubbles: true }));
    }

    function measure() {
      if (!cloned) { period = 0; return; }
      var first = track.children[0];
      var firstClone = track.children[originalCount];
      period = firstClone ? firstClone.offsetLeft - first.offsetLeft : 0;
    }

    function normalise() {
      if (period <= 0) return;
      while (pos >= period) pos -= period;
      while (pos < 0) pos += period;
    }

    function stepDistance() {
      var a = track.children[0];
      var b = track.children[1];
      if (a && b && b.offsetLeft > a.offsetLeft) return b.offsetLeft - a.offsetLeft;
      return Math.round(track.clientWidth * 0.8);
    }

    function step() {
      if (stepping) return;
      normalise();
      var from = pos;
      var target = from + stepDistance();

      /* Without clones there is no seam to cross: stop at the last card, and
         send the next click back to the first one. */
      if (!cloned) {
        var max = maxScroll();
        if (max <= 0) return;
        if (from >= max - 2) target = 0;
        else if (target > max) target = max;
      }

      var delta = target - from;
      if (delta === 0) return;
      var t0 = performance.now();
      stepping = true;

      function anim(now) {
        var p = Math.min(1, (now - t0) / STEP_MS);
        var eased = 1 - Math.pow(1 - p, 3);
        pos = from + delta * eased;
        write(pos);
        if (p < 1) {
          requestAnimationFrame(anim);
        } else {
          stepping = false;
          pos = target;
          normalise();
          write(pos);
        }
      }
      requestAnimationFrame(anim);
    }

    function holdThenResume(ms) {
      paused = true;
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () { paused = false; }, ms || 1600);
    }

    function frame(now) {
      if (lastTime === null) lastTime = now;
      var dt = (now - lastTime) / 1000;
      lastTime = now;

      /* period stays 0 unless clones exist, so a finite rail never drifts. */
      var running = autoplay && !paused && !stepping && onScreen &&
        period > 0 && !REDUCE.matches && !document.hidden;

      if (running) {
        /* dt is clamped so a backgrounded tab cannot jump the rail forward. */
        pos += speed * Math.min(dt, 0.05);
        normalise();
        write(pos);
      }
      requestAnimationFrame(frame);
    }

    function fit() {
      /* A finite rail still needs its arrow as soon as the row overflows; it
         just never gets clones. is-static hides the arrow when everything
         already fits, in both modes. */
      var overflowing = fill || hasOverflow();
      root.classList.toggle('is-static', !overflowing && !cloned);
      if (fill && !cloned) buildClones();
      measure();
    }

    fit();

    /* Anything that did not come from write() is a real user scroll. */
    track.addEventListener('scroll', function () {
      if (expected < 0 || Math.abs(track.scrollLeft - expected) > 3) {
        pos = track.scrollLeft;
      }
    }, { passive: true });

    root.addEventListener('mouseenter', function () { paused = true; });
    root.addEventListener('mouseleave', function () { paused = false; });
    root.addEventListener('focusin', function () { paused = true; });
    root.addEventListener('focusout', function () { paused = false; });
    track.addEventListener('pointerdown', function () { holdThenResume(2400); });
    track.addEventListener('wheel', function () { holdThenResume(2400); }, { passive: true });

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        step();
        holdThenResume(2400);
      });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
      }, { threshold: 0 }).observe(root);
    }

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        fit();
        pos = track.scrollLeft;
        normalise();
        write(pos);
      }, 200);
    });

    requestAnimationFrame(frame);
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-rail]').forEach(initRail);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* Theme editor re-renders the section on every setting change. */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
